import { ChevronRight, Home } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbsProps {
  items?: BreadcrumbItem[];
}

const Breadcrumbs = ({ items }: BreadcrumbsProps) => {
  const location = useLocation();
  
  // Auto-generate breadcrumbs if not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [
      { label: "Home", href: "/" }
    ];

    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      
      // Format segment for display
      let label = segment
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      
      // Custom labels for specific paths
      const customLabels: { [key: string]: string } = {
        'pay-monthly-travel': 'Pay Monthly for Travel',
        'pay-monthly-education': 'Pay Monthly for Education', 
        'pay-monthly-healthcare': 'Pay Monthly for Healthcare',
        'travel': 'Travel',
        'education': 'Education',
        'healthcare': 'Healthcare'
      };
      
      if (customLabels[segment]) {
        label = customLabels[segment];
      }
      
      // Don't make the last item clickable
      const isLast = index === pathSegments.length - 1;
      breadcrumbs.push({
        label,
        href: isLast ? undefined : currentPath
      });
    });

    return breadcrumbs;
  };

  const breadcrumbItems = items || generateBreadcrumbs();
  
  if (breadcrumbItems.length <= 1) return null;

  return (
    <nav 
      aria-label="Breadcrumb navigation" 
      className="py-4 px-4 bg-muted/30"
      role="navigation"
    >
      <div className="max-w-6xl mx-auto">
        <ol className="flex items-center space-x-2 text-sm">
          {breadcrumbItems.map((item, index) => (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <ChevronRight className="w-4 h-4 text-muted-foreground mx-2" aria-hidden="true" />
              )}
              
              {item.href ? (
                <Link 
                  to={item.href}
                  className="text-muted-foreground hover:text-primary transition-colors flex items-center"
                  aria-label={index === 0 ? "Navigate to homepage" : `Navigate to ${item.label}`}
                >
                  {index === 0 && <Home className="w-4 h-4 mr-1" aria-hidden="true" />}
                  {item.label}
                </Link>
              ) : (
                <span className="text-foreground font-medium flex items-center" aria-current="page">
                  {index === 0 && <Home className="w-4 h-4 mr-1" aria-hidden="true" />}
                  {item.label}
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default Breadcrumbs;